import React from "react";

/**
 * WaveDivider — a "shapedivider"-style transition between two solid sections.
 *
 * Renders layered, translucent waves so the section below appears to flow up
 * into the one above. Place it directly between two sections in the page flow.
 *
 *   topColor    – color of the section ABOVE (the divider's backdrop)
 *   bottomColor – color of the section BELOW (the bottom wave / seam color)
 *   waveColor   – flat accent tint for the decorative layers. Lets a divider
 *                 stay visible when topColor and bottomColor are near-identical.
 *                 Defaults to bottomColor. Ignored when `gradient` is set.
 *   gradient    – array of colors (top → bottom) for the decorative wave
 *                 layers. Use for color-theory fills (e.g. a warm sunset that
 *                 darkens into a dark section). The bottom-most seam wave still
 *                 uses bottomColor so the join with the next section is exact.
 *   height      – divider height in px (taller = more dramatic)
 *   animate     – slow horizontal drift on the back wave layer
 *
 * The drifting back layer reuses the existing `animate-marquee` keyframe
 * (translateX 0 → -50%); two identical tiles make the loop seamless.
 */
export default function WaveDivider({
  topColor = "#FFFFFF",
  bottomColor = "#FAFAF8",
  waveColor,
  gradient,
  height = 110,
  animate = true,
  className = "",
}) {
  const reactId = React.useId().replace(/:/g, "");
  const gradId = `wave-grad-${reactId}`;
  const useGrad = Array.isArray(gradient) && gradient.length >= 2;
  // Decorative layers: vertical gradient (theme) or flat accent tint.
  const waveFill = useGrad ? `url(#${gradId})` : waveColor || bottomColor;

  return (
    <div
      aria-hidden="true"
      className={`relative w-full overflow-hidden leading-[0] ${className}`}
      style={{ backgroundColor: topColor }}
    >
      {/* Shared gradient def — userSpaceOnUse so every wave layer maps to the
          same top→bottom axis (viewBox height 120) for a cohesive blend. */}
      {useGrad && (
        <svg width="0" height="0" className="absolute" aria-hidden="true">
          <defs>
            <linearGradient
              id={gradId}
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2="0"
              y2="120"
            >
              {gradient.map((c, i) => (
                <stop
                  key={i}
                  offset={`${(i / (gradient.length - 1)) * 100}%`}
                  stopColor={c}
                />
              ))}
            </linearGradient>
          </defs>
        </svg>
      )}

      {/* Back layer — two tiled, drifting copies for depth */}
      <div
        className={`flex w-[200%] ${animate ? "animate-marquee" : ""}`}
        style={{ height }}
      >
        {[0, 1].map((i) => (
          <svg
            key={i}
            className="h-full w-1/2 shrink-0"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            {/* Tileable path: y=60 at x=0 and x=1440 so copies seam cleanly */}
            <path
              d="M0,60 C360,0 360,120 720,60 C1080,0 1080,120 1440,60 V120 H0 Z"
              fill={waveFill}
              opacity="0.55"
            />
          </svg>
        ))}
      </div>

      {/* Foreground — two static layered waves anchored to the bottom */}
      <svg
        className="absolute inset-x-0 bottom-0 w-full"
        style={{ height }}
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
      >
        <path
          d="M0,72 C240,28 480,104 720,72 C960,40 1200,100 1440,64 L1440,120 L0,120 Z"
          fill={waveFill}
          opacity="0.85"
        />
        {/* Bottom-most wave keeps the true section color so the seam is exact */}
        <path
          d="M0,90 C220,118 480,66 720,90 C1000,116 1240,74 1440,96 L1440,120 L0,120 Z"
          fill={bottomColor}
        />
      </svg>
    </div>
  );
}
